<script lang="ts" setup>
  import { cva } from 'class-variance-authority'
  import type { IDisclaimer } from '@/components/ui/counter/types'
  import { marked } from 'marked'
  import { announce } from '@/utils/a11yAlert'

  const selected = defineModel<number>('modelValue', {
    required: true
  })

  const props = withDefaults(
    defineProps<{
      translationKey?: string | null
      maxNumber?: number
      minNumber?: number
      title?: string
      subTitle?: string
      disclaimer?: IDisclaimer
    }>(),
    {
      translationKey: null,
      maxNumber: 9,
      minNumber: 0,
      title: '',
      subTitle: '',
      disclaimer: () => ({
        variant: 'info',
        text: '',
        hasDisclaimer: false
      })
    }
  )

  const emit = defineEmits<{
    increase: []
    decrease: []
  }>()

  const { maxNumber, minNumber, disclaimer } = toRefs(props)

  const canIncrease = computed(() => selected.value < maxNumber.value)
  const canDecrease = computed(() => selected.value > minNumber.value)
  const { t } = useI18n()

  const increaseBtn = ref<HTMLElement | null>(null)
  const focusIncreaseBtn = () => {
    increaseBtn.value && increaseBtn.value.focus()
  }
  defineExpose({
    focusIncreaseBtn
  })

  const increaseNumber = async () => {
    if (canIncrease.value) {
      selected.value++
      emit('increase')
      announce(t(`paxSelector.${props.translationKey}.added`, { total: selected.value + 1 }))

      await nextTick()

      if (disclaimer.value?.hasDisclaimer) {
        announce(disclaimer.value.text)
      }
    }
  }

  const decreaseNumber = () => {
    if (canDecrease.value) {
      selected.value--
      emit('decrease')
      announce(t(`paxSelector.${props.translationKey}.removed`, { total: selected.value - 1 }))
    }
  }

  const increaseKeys = ['ArrowUp', 'ArrowRight', '=', '+']
  const decreaseKeys = ['ArrowDown', 'ArrowLeft', '-']

  const handleKeydown = (event: KeyboardEvent) => {
    if (![...increaseKeys, ...decreaseKeys].includes(event.key)) return

    if (increaseKeys.some((key) => key === event.key)) {
      increaseNumber()
    }

    if (decreaseKeys.some((key) => key === event.key)) {
      decreaseNumber()
    }
  }

  const increaseBtnBase = computed(() =>
    cva('flex size-6 select-none items-center justify-center rounded-full text-xl border', {
      variants: {
        disabled: {
          true: 'border-border text-border',
          false: 'border-bt-blue-ui bg-bt-blue-ui text-white'
        }
      }
    })({
      disabled: !canIncrease.value
    })
  )

  const decreaseBtnBase = computed(() =>
    cva('flex size-6 select-none items-center justify-center rounded-full text-xl border', {
      variants: {
        disabled: {
          true: 'border-border text-border',
          false: 'border-bt-blue-ui text-bt-blue-ui'
        }
      }
    })({
      disabled: !canDecrease.value
    })
  )

  const disclaimerText = computed(() => marked(disclaimer?.value.text))
</script>
<template>
  <div class="flex flex-col">
    <div class="flex size-full justify-between">
      <div
        v-if="title"
        class="flex flex-col justify-center"
      >
        <span class="text-bt-blue-dark text-subtitle-1">
          {{ title }}
        </span>
        <span
          v-if="subTitle"
          class="text-secondary-light text-body-2"
        >
          {{ subTitle }}
        </span>
      </div>
      <div class="flex items-center gap-2">
        <button
          :class="decreaseBtnBase"
          :disabled="!canDecrease"
          :aria-label="$t('aria.label.decrease', { title })"
          data-test="decrease-btn"
          @click="decreaseNumber"
        >
          <Icon
            name="common/remove"
            size="md"
          />
        </button>
        <span
          :aria-label="$t('aria.label.selectexPax', { count: selected, type: title })"
          class="flex w-4 justify-center text-center text-subtitle-1"
          @keydown="handleKeydown"
        >
          {{ selected }}
        </span>
        <button
          ref="increaseBtn"
          :class="increaseBtnBase"
          :disabled="!canIncrease"
          :aria-label="$t('aria.label.increase', { title })"
          data-test="increase-btn"
          @click="increaseNumber"
        >
          <Icon
            name="common/add"
            size="md"
          />
        </button>
      </div>
    </div>
    <UiAlert
      v-if="disclaimer?.hasDisclaimer"
      :variant="disclaimer.variant"
      :closable="false"
      class="mt-2"
    >
      <template #content>
        <span v-html="disclaimerText" />
      </template>
    </UiAlert>
  </div>
</template>
